import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The width Google Drive's preview player needs before it keeps its controls.
 *
 * Drive lays its transport out for the width it is given, and under about
 * 400px it mangles it: the caption and speed buttons are dropped, play is
 * exiled into a pill of its own, and the scrubber detaches from the row it
 * belongs to and floats above the picture. Measured on this page — 430px keeps
 * the whole row, 400px and below do not.
 */
export const PLAYER_DESIGN_WIDTH = 480;

/** What a 16:9 clip fills when it is fit to the design width. */
const PICTURE_HEIGHT = (PLAYER_DESIGN_WIDTH * 9) / 16;

/** Room for the transport Drive stacks below the picture. */
const CHROME_HEIGHT = 120;

/** The box Drive is laid out on: picture plus the strip under it. */
export const PLAYER_DESIGN_HEIGHT = PICTURE_HEIGHT + CHROME_HEIGHT;

/**
 * How much of each review is black bar rather than picture, top and bottom.
 *
 * ⚠ This is a property of the *files*, not of the layout, and it is the one
 *   number here that a new upload could invalidate. Nothing in the page can
 *   read it back: Firebase Storage serves these without CORS headers, so a
 *   frame drawn to a canvas is tainted and the padded 16:9 file dimensions say
 *   nothing about where the picture inside actually sits.
 *
 * ⚠ And the clips do not agree with each other. Measured with ffmpeg on all
 *   five, 2026-08-25: four of them are 1280x720 with footage on rows 180–540,
 *   exactly the middle half — but *Arjun Sen* carries footage on rows 114–606,
 *   a quarter more picture. One number has to serve both, and the two failures
 *   are not equal: too much crop cuts the tops off people's heads, too little
 *   leaves black above and below. Black on a black card is barely visible;
 *   a clipped face reads as a broken player. So this is set to the *smallest*
 *   bar any clip has, and the four with more simply carry a thin band of their
 *   own black — which is invisible against `bg-ink-950`.
 *
 *   The clean fix is not a better number, it is re-exporting the clips with
 *   the bars cropped out, at which point this goes to 0.
 */
export const CLIP_LETTERBOX = 0.158;

/** The picture band that is left once the baked-in bars are cropped away. */
export const PLAYER_BAND_HEIGHT = PICTURE_HEIGHT * (1 - 2 * CLIP_LETTERBOX);

/**
 * How much larger than the card the picture is played at.
 *
 * Cropping the bars away leaves a wide ribbon, and a ribbon the width of the
 * card is a small thing on a phone. The card cannot get wider, so the only way
 * the picture gets bigger is to play it larger than the frame and lose the
 * sides — the footage keeps its proportions, there is simply less of it across.
 *
 * ⚠ 1.1 is as far as this can go, and the limit is the footage rather than
 *   taste. Every review is a two-up call, one speaker in each half of the
 *   frame. Four of them leave dead space between the tiles that a crop can eat
 *   into; *Arjun Sen* does not — both faces run nearly to the edges. Rendered
 *   and looked at, 2026-08-25: 1.1 clears both, 1.2 already has his face
 *   touching the left edge and hers crowding the right.
 *
 *   This was briefly 1.5, which together with a 0.25 letterbox is what made
 *   that review look, in the user's words, "pretty much cropped" — it lost the
 *   tops of both heads and a sixth of the width off each side.
 */
export const CLIP_ZOOM = 1.1;

/**
 * The frame is a window on the middle of the design box.
 *
 * Drive centres the video in the box it is given and lays its transport over
 * the foot of it, and the clip centres its own picture inside itself — so the
 * picture band is centred on the design box, and a window about that centre
 * shows the footage and nothing else: no transport, and none of the black the
 * clip carries. `CLIP_ZOOM` then trades width for height, which is why the
 * window is not simply the band's own shape.
 */
export const playerFrameStyle = {
  aspectRatio: `${PLAYER_DESIGN_WIDTH} / ${PLAYER_BAND_HEIGHT * CLIP_ZOOM}`,
};

/**
 * The same window, on a player we actually own.
 *
 * A cross-origin frame has to be laid out at a width it survives and scaled
 * back, which is the whole apparatus below. A native `<video>` has none of
 * that: no transport of its own to hide, and no coordinate space to be
 * confused about — so the crop is one line of arithmetic instead.
 *
 * The element is laid out `CLIP_ZOOM` times the frame's width and centred, and
 * the frame's `overflow: hidden` does the rest. At 1.2 that puts exactly the
 * middle half of the picture — the band the clips carry footage in — across
 * the frame's full height, so the black the files are padded with falls
 * outside it, and takes 8% off each side to buy the extra height.
 *
 * `object-fit: cover` is the safety net rather than the mechanism: the reviews
 * are all 16:9, and a clip that is not simply fills the box instead of
 * letterboxing itself inside it a second time.
 */
export const playerVideoStyle = {
  position: 'absolute',
  left: '50%',
  top: '50%',
  width: `${CLIP_ZOOM * 100}%`,
  // Tailwind's reset caps media at `max-width: 100%`, which is exactly the one
  // rule this crop cannot live with — clamped back to the frame's own width the
  // picture keeps its black bars and the zoom does nothing at all.
  maxWidth: 'none',
  aspectRatio: '16 / 9',
  objectFit: 'cover',
  transform: 'translate(-50%, -50%)',
};

/**
 * Lay a cross-origin player out at a width it survives, then shrink it to fit.
 *
 * A phone column is never 480px wide — 333px at a 375px viewport — so on a
 * phone every Drive review came up with the broken chrome. The player is a
 * cross-origin document, so its controls cannot be restyled, only sized: the
 * frame is given the design width above and the whole thing, picture and
 * controls together, is scaled back down to the box we actually have.
 *
 * ⚠ The factor is measured here rather than derived in CSS from container
 *   units, and that is not fussiness. `zoom: min(1, 100cqw / 480px)` reads
 *   correctly and works in Chrome, but Safari resolves container units in the
 *   *zoomed* coordinate space — so the factor feeds back into itself and comes
 *   out squared, leaving the player short of its box by exactly the amount it
 *   was scaled by. A plain number has no coordinate space to be confused about.
 *
 * Nothing here trusts a single reading. The first measurement can land before
 * the column has been laid out — a card with no width yet reports the frame as
 * its negative margins alone, 40px — so the observer, a deferred second look
 * and the resize listener all re-measure, and any of them corrects it.
 *
 * @returns {[Function, number]} ref callback for the frame, and the scale to
 *   apply to the stage inside it — 0 until a measurement lands.
 */
export function usePlayerScale() {
  const nodeRef = useRef(null);
  const [scale, setScale] = useState(0);

  /** Kept on the instance so every path re-measures the *live* node. */
  const measure = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;

    const { width } = node.getBoundingClientRect();
    // Nothing to go on yet; a later pass covers it.
    if (!width) return;

    // The base factor never scales *up*: past the design width the player is
    // already native size. `CLIP_ZOOM` on top of it is the deliberate one.
    setScale(Math.min(1, width / PLAYER_DESIGN_WIDTH) * CLIP_ZOOM);
  }, []);

  const observerRef = useRef(null);

  // A ref callback rather than a plain ref: the observer follows the node it
  // is given, so a remount can never leave it watching a detached element.
  const ref = useCallback(
    (node) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      nodeRef.current = node;
      if (!node) return;

      measure();

      // The first reading runs in whatever layout state the mount happens to
      // be in. Take a second one once the browser has drawn a frame.
      if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(measure);

      if (typeof ResizeObserver !== 'undefined') {
        observerRef.current = new ResizeObserver(measure);
        observerRef.current.observe(node);
      }
    },
    [measure]
  );

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [measure]);

  // Deliberately un-gated: every render re-measures. `setScale` is a no-op at
  // the same value, so this settles immediately rather than looping, and it
  // means the scale is corrected by anything that re-renders the player — the
  // review starting, the day being cleared — without waiting on an observer.
  useEffect(measure);

  return [ref, scale];
}

/**
 * The style that puts the player on its design box and scales it back.
 *
 * `zoom` rather than `transform: scale()`, though both paint the same result.
 * Zoom is a layout-level scale: the frame genuinely *is* 480px wide to the
 * document inside it and card-width to the page outside it, so pointers are
 * mapped by the ordinary layout path. A transform leaves a 480px layout box
 * painted small and leans on the compositor to map every tap back through the
 * matrix — a lot to ask of a cross-origin player whose controls are the whole
 * point of the exercise.
 *
 * Before a measurement lands there is nothing to scale by, so the stage is
 * left to the stylesheet, which simply fills the frame.
 */
export function playerStageStyle(scale) {
  if (!scale) return undefined;

  return {
    inset: 'auto',
    // Pulled up and left so the frame sits over the middle of the design box.
    // Both are in the stage's own (zoomed) units, which is why neither depends
    // on how wide the card happens to be.
    top: `${-(PLAYER_DESIGN_HEIGHT - PLAYER_BAND_HEIGHT) / 2}px`,
    left: `${(-PLAYER_DESIGN_WIDTH * (CLIP_ZOOM - 1)) / (2 * CLIP_ZOOM)}px`,
    width: `${PLAYER_DESIGN_WIDTH}px`,
    height: `${PLAYER_DESIGN_HEIGHT}px`,
    zoom: scale,
  };
}
